import { useQuery } from "@tanstack/react-query";
import { useParams, useSearchParams } from "react-router-dom";
import { t } from "ttag";

import { ScreenLayout } from "@/app/components/layouts";
import { usePatientQuery } from "@/app/hooks";
import { usePortalUserStore } from "@/app/stores";
import { consultationsQueriesStore } from "@/features/consultations/api";
import {
  ScheduleContextProvider,
  useConsultationStore,
} from "@/features/consultations/contexts";
import {
  APPOINTMENT_TYPE,
  appointmentSchema,
} from "@/features/consultations/models";
import {
  ScheduleAvailableDaysCard,
  ScheduleAvailableTimeSlotCard,
  ScheduleProviderPreferenceCard,
  ScheduleSubmit,
} from "@/features/consultations/sections";
import { LogoLoader } from "@/shared/components";
import { CATEGORY_SLUG } from "@/shared/domains";
import { useSettingsStore } from "@/shared/stores";
import { Card } from "@/shared/ui";
import { LanguagePreference, ScheduleConsultationHeader } from "./sections";

export const ConsultationSchedule = () => {
  const language = useSettingsStore((state) => state.language);
  const appointmentTypeFromStore = useConsultationStore(
    (state) => state.appointmentType,
  );
  const subscriptions = usePortalUserStore(
    (state) => state.portalUser?.subscriptions,
  );

  const { data } = usePatientQuery();
  const state = data?.location?.state;

  const { appointmentId } = useParams();

  const [searchParams] = useSearchParams();
  const subscriptionIdParam = searchParams.get("subscriptionId");

  const parsedAppointmentType = appointmentSchema.shape.type.safeParse(
    searchParams.get("type"),
  ).data;

  const appointmentType = parsedAppointmentType ?? appointmentTypeFromStore;

  const isReschedule = Boolean(appointmentId);

  const rescheduleParams =
    (parsedAppointmentType || subscriptionIdParam) && appointmentType != null
      ? {
          appointmentType,
          subscriptionId: subscriptionIdParam
            ? Number(subscriptionIdParam)
            : undefined,
        }
      : undefined;

  const {
    data: appointmentToReschedule,
    isLoading: isLoadingAppointmentToReschedule,
    isSuccess: isSuccessAppointmentToReschedule,
  } = useQuery(
    consultationsQueriesStore.consultations.appointments._ctx.detail(
      appointmentId,
      rescheduleParams,
    ),
  );

  const matchingSubscription = isReschedule
    ? subscriptions?.find(
        (subscription) => subscription.id === Number(subscriptionIdParam),
      )
    : subscriptions?.find(
        (subscription) =>
          (subscription.product.category.slug === CATEGORY_SLUG.WEIGHT_LOSS ||
            subscription.product.category.slug === CATEGORY_SLUG.MICRODOSING) &&
          appointmentType !== APPOINTMENT_TYPE.OTHER,
      );

  const subscriptionId = matchingSubscription?.id;

  return (
    <ScreenLayout>
      <ScheduleConsultationHeader isReschedule={isReschedule} />

      <LogoLoader show={isLoadingAppointmentToReschedule} withOverlay />

      {(isSuccessAppointmentToReschedule || !appointmentId) && (
        <ScheduleContextProvider
          defaultLanguage={language}
          appointmentType={appointmentType ?? APPOINTMENT_TYPE.OTHER}
          state={state}
          appointmentToReschedule={appointmentToReschedule}
          subscriptionId={subscriptionId}
          vendor={matchingSubscription?.vendor}
        >
          <LanguagePreference />

          <ScheduleProviderPreferenceCard
            title={`2. ${t`Provider preference`}`}
          />

          <div className="grid gap-x-3 gap-y-8 xl:grid-cols-2">
            <ScheduleAvailableDaysCard
              title={`3. ${t`Choose an available day`}`}
            />

            <ScheduleAvailableTimeSlotCard titleStep={4}>
              <Card.Footer>
                <ScheduleSubmit />
              </Card.Footer>
            </ScheduleAvailableTimeSlotCard>
          </div>
        </ScheduleContextProvider>
      )}
    </ScreenLayout>
  );
};
